// Catalog grid for the Try-On "Catalog" tab. Lets users pick a curated
// product image instead of uploading or pasting a link.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  PRODUCT_TEMPLATES,
  CATEGORY_LABELS,
  BRAND_LABELS,
  type ProductCategory,
  type ProductTemplate,
} from "@/lib/product-templates";
import { cn } from "@/lib/utils";
import { ImageWithSkeleton } from "@/components/ui/image-with-skeleton";

interface Props {
  onPick: (template: ProductTemplate) => void;
  selectedId?: string | null;
  /** Compact mode shrinks tiles for the mobile composer. */
  compact?: boolean;
}

const CATS: (ProductCategory | "all")[] = [
  "all",
  "shoes",
  "shirt",
  "top",
  "bottom",
  "dress",
  "jacket",
];

const PAGE_SIZE = 20;

export const ProductTemplatePicker = ({ onPick, selectedId, compact = false }: Props) => {
  const [cat, setCat] = useState<ProductCategory | "all">("all");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const chipRowRef = useRef<HTMLDivElement | null>(null);

  const handleCatClick = (c: ProductCategory | "all") => {
    setCat(c);
    // Scroll only the chip row horizontally (not the page) so the clicked
    // chip is centered. Using scrollLeft avoids scrollIntoView bubbling up
    // to ancestor scroll containers / the window.
    requestAnimationFrame(() => {
      const row = chipRowRef.current;
      const chip = chipRefs.current[c];
      if (!row || !chip) return;
      const target = chip.offsetLeft - row.clientWidth / 2 + chip.clientWidth / 2;
      row.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    });
  };

  const items = useMemo(
    () => (cat === "all" ? PRODUCT_TEMPLATES : PRODUCT_TEMPLATES.filter((p) => p.category === cat)),
    [cat],
  );

  // Reset pagination when the category changes
  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [cat]);

  const shown = items.slice(0, visible);
  const hasMore = visible < items.length;

  return (
    <div className="flex w-full flex-col">
      {/* Category chips */}
      <div ref={chipRowRef} className="mb-3 -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {CATS.map((c) => {
          const active = cat === c;
          return (
            <button
              key={c}
              ref={(el) => { chipRefs.current[c] = el; }}
              type="button"
              onClick={() => handleCatClick(c)}
              className={cn(
                "shrink-0 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] transition-all",
                active
                  ? "border-primary bg-primary/15 text-primary shadow-[0_0_12px_-4px_hsl(var(--primary))]"
                  : "border-white/10 bg-white/[0.03] text-muted-foreground hover:border-white/20 hover:text-foreground",
              )}
            >
              {CATEGORY_LABELS[c]}
            </button>
          );
        })}
      </div>

      {/* Tile grid — page-based, no inner scroll. Smaller tiles per user request. */}
      <div
        className={cn(
          "grid gap-2",
          compact ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8",
        )}
      >
        {shown.map((t, idx) => {
          const selected = selectedId === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onPick(t)}
              className={cn(
                "group relative flex flex-col overflow-hidden rounded-xl border-2 bg-white text-left transition-all hover:scale-[1.02] active:scale-[0.98]",
                selected
                  ? "border-primary shadow-[0_0_18px_-4px_hsl(var(--primary))]"
                  : "border-white/10 hover:border-primary/60",
              )}
              aria-label={`Try ${t.label}`}
            >
              <div className="relative aspect-square w-full bg-white">
                <ImageWithSkeleton
                  src={t.image}
                  alt={t.label}
                  objectFit="contain"
                  imageClassName="p-3"
                  eager={idx < 6}
                />
                <span className="absolute right-1.5 top-1.5 rounded bg-background/85 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-foreground backdrop-blur-md">
                  {BRAND_LABELS[t.brand]}
                </span>
              </div>
              <span className="truncate border-t border-border/40 bg-card/60 px-2 py-1.5 text-[11px] font-semibold text-foreground">
                {t.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Load more / status */}
      <div className="mt-3 flex flex-col items-center gap-1">
        {hasMore ? (
          <button
            type="button"
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className="rounded-full border border-primary/40 bg-primary/10 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary transition-all hover:bg-primary/20 hover:shadow-[0_0_14px_-4px_hsl(var(--primary))] active:scale-[0.98]"
          >
            Load more
          </button>
        ) : (
          items.length > PAGE_SIZE && (
            <button
              type="button"
              onClick={() => setVisible(PAGE_SIZE)}
              className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground transition-all hover:text-foreground"
            >
              Show less
            </button>
          )
        )}
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Showing {shown.length} of {items.length}
        </p>
        <p className="mt-1 max-w-xs text-center text-[10px] leading-snug text-muted-foreground/70">
          Tiles are AI-generated style references. Tap one to find similar real products on partner stores.
        </p>
      </div>
    </div>
  );
};
